import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { track } from "../utils/analytics";

import LaunchBanner from "../components/LaunchBanner";
import Header from "../components/Header";
import { venueDetailsMapping } from "../misc/venueDetailsMapping";

// SimpleBar
import SimpleBar from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";

import "../styles/CheckVenue.css";

const PAGE_SIZE = 25;

const CheckVenue = () => {
  const navigate = useNavigate();

  const venues = useMemo(() => {
    return Object.entries(venueDetailsMapping).map(([id, v]) => ({
      id,
      name: v.title || id,
      city: v.city || "",
      state: v.state || "",
      photoUrl: v.imageUrl || "",
    }));
  }, []);

  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return venues;
    return venues.filter(
      (v) =>
        v.name.toLowerCase().includes(ql) ||
        (v.city && v.city.toLowerCase().includes(ql)) ||
        (v.state && v.state.toLowerCase().includes(ql))
    );
  }, [q, venues]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pagedVenues = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  useEffect(() => {
    setPage(1);
  }, [q]);

  const goToSignup = () => {
    track("nav_click", { location: "check_venue_header_signup" });
    navigate("/signup");
  };

  return (
    <div className="check-venue-root">
      <LaunchBanner goToSignup={goToSignup} />
      <Header />

      <div className="check-venue-page section-container">
        <div className="cv-head">
          <h1 className="check-venue-title">Check if we cover your venue</h1>
          <p className="check-venue-desc">
            We currently support {venues.length} venues and counting. Search
            below to see if yours is included.
          </p>

          <input
            type="text"
            className="venue-search-input"
            placeholder="Search by venue name, city, or state..."
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              track("venue_search_input", { query: e.target.value });
            }}
          />
        </div>

        {/* Bordered frame */}
        <div className="venue-panel">
          {/* SimpleBar replaces the manual scroll div */}
          <SimpleBar
            autoHide={false}
            style={{ maxHeight: 460 }}
            className="venue-simplebar"
          >
            {pagedVenues.length === 0 ? (
              <div className="no-results">
                <p>No venues found.</p>
              </div>
            ) : (
              <ul className="venue-list">
                {pagedVenues.map((v) => (
                  <li
                    key={v.id}
                    className="venue-row"
                    onClick={() => {
                      track("venue_row_click", { venue_id: v.id });
                      // navigate(`/signup?venue=${encodeURIComponent(v.name)}`);
                    }}
                  >
                    {v.photoUrl && (
                      <img
                        src={v.photoUrl}
                        alt={v.name}
                        className="venue-thumb"
                        loading="lazy"
                      />
                    )}
                    <div className="venue-info">
                      <h4>{v.name}</h4>
                      <p>
                        {[v.city, v.state].filter(Boolean).join(", ") || "—"}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </SimpleBar>
        </div>

        {/* Pagination */}
        {filtered.length > PAGE_SIZE && (
          <div className="pagination-bar">
            <button
              className="btn btn-tertiary btn-auto"
              disabled={page === 1}
              onClick={() => {
                setPage((p) => p - 1);
                track("venue_page_prev", { page: page - 1 });
              }}
            >
              ← Prev
            </button>

            <span className="pagination-info">
              Page {page} of {totalPages}
            </span>

            <button
              className="btn btn-tertiary btn-auto"
              disabled={page === totalPages}
              onClick={() => {
                setPage((p) => p + 1);
                track("venue_page_next", { page: page + 1 });
              }}
            >
              Next →
            </button>
          </div>
        )}

        {/* CTA */}
        <div className="request-venue-cta">
          <h3>Don’t see your venue?</h3>
          <p>We’ll add it within 24 hours.</p>
          <button
            className="btn btn-primary btn-auto"
            onClick={() => {
              track("venue_request_click_main");
              navigate("/request-venue");
            }}
          >
            Request a venue
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckVenue;
